import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, Icon } from '@/shared/ui';

import { ensureRefreshImageSrc } from '@/entities/assets';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';

import { useHideReport, useRestoreReport } from '@/features/hideReport';
import {
  getReportPreviewTestIDSegment,
  ReportCardLayout,
  StructuredReportsAndStudiesType,
} from '@/features/reportCards';

import { useReportCard } from '../../hooks';
import { sanitizeForTestID } from '../../lib';
import { DetailsPopover } from '../DetailsPopover/DetailsPopover';
import { ReportGenerationStatus } from '../ReportGenerationStatus/ReportGenerationStatus';

import styles from './ReportCard.module.scss';

type ReportCardProps = {
  structuredReportsAndStudies: StructuredReportsAndStudiesType;
  className?: string;
  testID?: string;
};

export const ReportCard: FC<ReportCardProps> = (props) => {
  const { structuredReportsAndStudies, className, testID } = props;

  const dispatch = useAppDispatch();
  const { formatMessage, formatDate } = useIntl();
  const [isRemoving, setIsRemoving] = useState(false);
  const {
    isReportGenerationCompleted,
    isIOXRayOrPanowings,
    isPanowingsReport,
    reportType,
    date,
    report,
    isReportFailed,
    isReportSigned,
    isReportOrderAvailable,
    reorderFunding,
    isLoadingPDF,
    cardPreviewSrc,
    isIntraoralScanReport,
    CanRequestReport,
    shouldHideDownload,
    reportTitle,
    handleCardClick,
    handleMiddleClick,
    reorderReport,
    downloadReportHandler,
    removeReportHandler,
    superimpositionOrientation,
  } = useReportCard(structuredReportsAndStudies);

  const hideReport = useHideReport();
  const restoreReport = useRestoreReport();

  const enableArchiveReportsFeature = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ArchiveReports,
    ),
  );

  if (!report) {
    return;
  }

  const reportPreviewTestIDSegment = getReportPreviewTestIDSegment(reportType);

  const {
    CanArchive = false,
    CanDelete = false,
    CanRestore = false,
  } = report.YourPermissions ?? {};
  const isReportHidden = Boolean(report.Archived?.Archived);

  const handleRemoveWithAnimation = async () => {
    const confirmed = await removeReportHandler({
      onError: () => setIsRemoving(false),
    });

    if (confirmed) {
      setIsRemoving(true);
    }
  };

  const handleHideWithAnimation = async () => {
    const confirmed = await hideReport(
      structuredReportsAndStudies.activeReportIDs,
      { onError: () => setIsRemoving(false) },
    );

    if (confirmed) {
      setIsRemoving(true);
    }
  };

  const handleRestoreWithAnimation = async () => {
    setIsRemoving(true);

    const restored = await restoreReport(
      structuredReportsAndStudies.activeReportIDs,
    );

    if (!restored) {
      setIsRemoving(false);
    }
  };

  // A hidden report can only be deleted for good, and only by its owner; an
  // active one is hidden while the archive feature is on, deleted otherwise.
  const getMobileRemoveReportHandler = () => {
    if (isReportHidden) {
      return CanDelete ? handleRemoveWithAnimation : undefined;
    }

    return enableArchiveReportsFeature
      ? handleHideWithAnimation
      : handleRemoveWithAnimation;
  };

  const openReportInfoModal = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.ReportInfo,
        data: {
          sourceStudyIDs: report.SourceStudyIDs,
          reportType,
          orientation: superimpositionOrientation,
          shouldHideDownload,
          hideMode: enableArchiveReportsFeature,
          isHidden: isReportHidden,
          removeReport: getMobileRemoveReportHandler(),
          restoreReport:
            isReportHidden && CanRestore
              ? handleRestoreWithAnimation
              : undefined,
          downloadReport: downloadReportHandler,
        },
      }),
    );
  };

  return (
    <ReportCardLayout
      className={className}
      date={
        date
          ? formatDate(date, {
              dateStyle: 'medium',
            })
          : formatMessage({
              id: 'reportCard.unknownDate',
              defaultMessage: 'Unknown date',
            })
      }
      desktopActions={
        <DetailsPopover
          canArchive={CanArchive}
          canDelete={CanDelete}
          canRestore={CanRestore}
          hideMode={enableArchiveReportsFeature}
          isHidden={isReportHidden}
          isReportGenerationCompleted={Boolean(isReportGenerationCompleted)}
          isLoadingPDF={isLoadingPDF}
          shouldHideDownload={shouldHideDownload}
          onDetailsView={openReportInfoModal}
          onRemoveReport={handleRemoveWithAnimation}
          onDownloadReport={downloadReportHandler}
          onHideReport={handleHideWithAnimation}
          onRestoreReport={handleRestoreWithAnimation}
        />
      }
      isGenerationCompleted={Boolean(isReportGenerationCompleted)}
      isHidden={isReportHidden}
      isRemoving={isRemoving}
      onAuxClick={(event) => {
        if (event.button === 1) {
          event.stopPropagation();
          handleMiddleClick();
        }
      }}
      onClick={handleCardClick}
      onImageError={ensureRefreshImageSrc}
      onMobileActionsClick={
        isReportHidden && !CanRestore && !CanDelete
          ? undefined
          : (event) => {
              event.stopPropagation();
              openReportInfoModal();
            }
      }
      previewAlt={reportTitle}
      previewClassName={cn(reportType)}
      previewSrc={cardPreviewSrc}
      previewTestID={
        reportPreviewTestIDSegment
          ? `preview-report-${sanitizeForTestID(
              reportPreviewTestIDSegment,
            )}-${report.ID}`
          : undefined
      }
      shouldContainPreview={isIOXRayOrPanowings}
      shouldUseLightBackground={isIntraoralScanReport}
      testID={testID}
      title={reportTitle}
    >
      {!isReportGenerationCompleted && (
        <ReportGenerationStatus report={report} />
      )}

      {(isReportSigned || isReportFailed) && (
        <footer className={cn(styles.footer, 'p2')}>
          {isReportSigned && (
            <div className={styles.signed}>
              <Icon name="check" size={24} />

              <FormattedMessage
                id="reportCard.signed"
                defaultMessage="Signed"
              />
            </div>
          )}

          {isReportFailed && CanRequestReport && !isPanowingsReport && (
            <Button
              variant="secondary"
              onClick={(event) => {
                event.stopPropagation();

                reorderReport();
              }}
              size="medium"
              className={styles.reorder}
              disabled={
                isReportOrderAvailable &&
                reorderFunding.source !== 'diagnocoins'
              }
            >
              {reorderFunding.source === 'diagnocoins' ? (
                <FormattedMessage
                  id="reportCard.regenerateForCoins"
                  defaultMessage="Regenerate for {coins, plural, one {# coin} other {# coins}}"
                  values={{ coins: reorderFunding.coins }}
                />
              ) : (
                <FormattedMessage
                  id="reportCard.regenerate"
                  defaultMessage="Regenerate"
                />
              )}
            </Button>
          )}
        </footer>
      )}
    </ReportCardLayout>
  );
};
